import type { ContainerType } from "@shared/types/supabase.types";
import { queryOptions } from "@tanstack/react-query";
import type { MemberActorType } from "../containers.types";
import * as containersApi from "./containers-api";

export const containerQueries = {
  all: () => ["containers"] as const,
  lists: () => [...containerQueries.all(), "list"] as const,
  details: () => [...containerQueries.all(), "detail"] as const,
  // CONTAINER KEYS
  byId: (containerId: string) =>
    queryOptions({
      queryKey: [...containerQueries.details(), containerId] as const,
      queryFn: () => containersApi.getContainerByContainerId(containerId),
    }),
  // TODO: remove workspacesByUser key and use more general byUserAndContainerType
  workspacesByUser: (userId: string) =>
    queryOptions({
      queryKey: [...containerQueries.lists(), userId, "workspace"] as const,
      queryFn: () => containersApi.getContainersOfUserAndContainerType(userId, "workspace"),
    }),
  projectsByUserAndParentContainerId: (userId: string, parentContainerId: string) =>
    queryOptions({
      queryKey: [
        ...containerQueries.lists(),
        userId,
        "project",
        "parent_container_id",
        parentContainerId,
      ] as const,
      queryFn: () => containersApi.getProjectsOfUserByParentContainerId(userId, parentContainerId),
    }),
  listByUserAndType: (userId: string, containerType: ContainerType) =>
    queryOptions({
      queryKey: [...containerQueries.lists(), userId, containerType] as const,
      queryFn: () => containersApi.getContainersOfUserAndContainerType(userId, containerType),
    }),
  workspaceBySlug: (slug: string) =>
    queryOptions({
      queryKey: [...containerQueries.details(), "workspace", "slug", slug] as const,
      queryFn: () => containersApi.getWorkspaceBySlug(slug),
    }),
  /**
   * CR-183: the caller's standing on the workspace (my_workspace_access).
   * Keyed by user so an account switch in the same tab never reuses it.
   */
  myWorkspaceAccess: (userId: string, workspaceId: string) =>
    queryOptions({
      queryKey: [
        ...containerQueries.details(),
        "workspace",
        workspaceId,
        "my-access",
        userId,
      ] as const,
      queryFn: () => containersApi.getMyWorkspaceAccess(workspaceId),
      staleTime: 30_000,
    }),
  projectBySlugs: (workspaceSlug: string, projectSlug: string) =>
    queryOptions({
      queryKey: [
        ...containerQueries.details(),
        "project",
        "slug",
        workspaceSlug,
        projectSlug,
      ] as const,
      queryFn: () => containersApi.getProjectBySlugs(workspaceSlug, projectSlug),
    }),
  // CONTAINER MEMBERS KEYS
  memberships: () => [...containerQueries.all(), "memberships"] as const,
  // TODO: remove (memberId: string) and replace by (userId: string, containerId: string), call it member
  membershipById: (memberId: string) =>
    queryOptions({
      queryKey: [...containerQueries.memberships(), memberId] as const,
      queryFn: () => containersApi.getContainerMemberById(memberId),
    }),
  membershipByUserAndContainer: (userId: string, containerId: string) =>
    queryOptions({
      queryKey: [...containerQueries.memberships(), containerId, userId] as const,
      queryFn: () => containersApi.getContainerMemberByContainerId(userId, containerId),
    }),
  membersByContainer: <T extends MemberActorType>(containerId: string, actorTypes: readonly T[]) =>
    queryOptions({
      queryKey: [...containerQueries.memberships(), containerId, actorTypes] as const,
      queryFn: () => containersApi.getContainerMembers(containerId, actorTypes),
    }),
};
